import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { FormEntryTextField } from '@/features/privateCompanies/components/SpecialEntries/FormEntryTextField'
import { useCreateCompanySpecialEntry } from '@/features/privateCompanies/hooks/useCreateCompanySpecialEntry'
import {
  CreateSpecialEntryValidationSchema,
  createSpecialEntrySchema,
} from '@/features/privateCompanies/schemas/createSpecialEntry.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ModalCreateSpecialEntryProps {
  companyType?: string
  operationKlassToCheckPermission?: string
}

export const ModalCreateSpecialEntry = ({ companyType, operationKlassToCheckPermission }: ModalCreateSpecialEntryProps) => {
  const { id: companyId } = useParams()
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchEstablishment, setSearchEstablishment] = useDebounce('')
  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishment },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({
        valueFieldName: 'id',
        addAllEstablishmentOption: true,
      }),
    }),
  })

  const { data: feeTemplates } = useFeeTemplates<FeeTemplateData[]>({
    filters: { search: searchFeeTemplate, category: companyType },
  })

  const useFormMethods = useForm<CreateSpecialEntryValidationSchema>({
    resolver: zodResolver(createSpecialEntrySchema),
  })

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
    setError,
  } = useFormMethods

  const onSubmit: SubmitHandler<CreateSpecialEntryValidationSchema> = (data) => {
    createCompanySpecialEntry(data)
  }

  const onSuccess = () => {
    resetForm()
    queryClient.invalidateQueries({ queryKey: ['company', companyId] })
    setIsOpen(false)
  }

  const onError = (error: any) => {
    if (error.response?.data.errors.base || error.response?.data.errors['fee_rule.special_entries.fee_rule_id'] || error.response?.data.errors['fee_rule.special_entries.base']) {
      setMessages(
        error.response?.data.errors.base || error.response?.data.errors['fee_rule.special_entries.fee_rule_id'] || error.response?.data.errors['fee_rule.special_entries.base'],
      )
    } else {
      FormTransformers.errorsTransformer({ setError, setMessages })(error)
    }
  }

  const {
    mutate: createCompanySpecialEntry,
    isPending: createCompanySpecialEntryIsPending,
    isError: createCompanySpecialEntryIsError,
  } = useCreateCompanySpecialEntry({
    companyId,
    onSuccess,
    onError,
  })

  useEffect(() => {
    setTimeout(resetForm, 500);
  }, [isOpen]);

  return (
    <>
      {messages ? (
        <ModalMessageSimple messages={messages} setMessages={setMessages} isError={createCompanySpecialEntryIsError} />
      ) : (
        <Modal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          onConfirm={handleSubmit(onSubmit)}
          Trigger={<Button operationKlassToCheckPermission={operationKlassToCheckPermission} className="ml-auto">Adicionar Tarifa Especial</Button>}
          title="Adicionar Tarifa Especial"
          footer="Salvar"
          body={
            <FormProvider {...useFormMethods}>
              <form className="flex flex-col gap-4">
                <SelectController
                  control={control}
                  name="target_id"
                  label="Estabelecimento"
                  onInputChange={setSearchEstablishment}
                  options={establishmentOptions || []}
                  errorMessage={errors.target_id?.message}
                  isDisabled={createCompanySpecialEntryIsPending}
                  markAsRequired
                />

                <SelectController
                  control={control}
                  name="fee_template_id"
                  label="Tarifa"
                  onInputChange={setSearchFeeTemplate}
                  options={
                    Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' })(
                      feeTemplates || [],
                    ) || []
                  }
                  onChange={(e) => {
                    const { label, value } = e as Option
                    setValue('fee_template_id', { label, value })
                    setValue(
                      'calculate_as',
                      feeTemplates?.find((template) => template.id === value)?.calculate_as || '',
                    )
                    setValue('value', '')
                  }}
                  errorMessage={errors.fee_template_id?.message}
                  isDisabled={createCompanySpecialEntryIsPending}
                  markAsRequired
                />

                {!!watch('calculate_as') && (
                  <FormEntryTextField disabled={createCompanySpecialEntryIsPending} className="w-full" />
                )}
              </form>
            </FormProvider>
          }
        />
      )}
    </>
  )
}
